// CTV-466 follow-up: shared inline banner used across the advertiser-facing
// self-serve app. Modeled on CancelCampaignModal's "Future payments canceled"
// callout — rounded-xl with a thin vertical rail on the left and an uppercase
// label above the body. Tones share the same shape so every callout reads
// with the same scan-pattern.

import React from 'react';

export type BannerTone = 'info' | 'warn' | 'success' | 'caution' | 'orange';

interface BannerProps {
  tone?: BannerTone;
  label?: React.ReactNode;
  // When tone === 'info', the station accent drives the rail + label + tint.
  // Other tones ignore this prop and use their fixed tonal palette.
  accentColor?: string;
  // Optional leading icon. Rendered just before the label at uppercase scale.
  icon?: React.ReactNode;
  // When set, an absolute-positioned action lives on the right (e.g., a
  // dismiss "×" button). Renders aligned to the top-right of the card.
  rightSlot?: React.ReactNode;
  // Tightens the vertical padding for compact one-liner banners.
  compact?: boolean;
  className?: string;
  children?: React.ReactNode;
}

interface TonePalette {
  background: string;
  border: string;
  rail: string;
  label: string;
}

function paletteFor(tone: BannerTone, accentColor: string | undefined): TonePalette {
  switch (tone) {
    case 'warn':
      return {
        background: 'linear-gradient(180deg, #fef2f2 0%, #fee2e2 100%)',
        border: 'rgba(220, 38, 38, 0.18)',
        rail: '#dc2626',
        label: '#b91c1c',
      };
    case 'success':
      return {
        background: 'linear-gradient(180deg, #ecfdf5 0%, #d1fae5 100%)',
        border: 'rgba(16, 185, 129, 0.18)',
        rail: '#10b981',
        label: '#047857',
      };
    case 'caution':
      return {
        background: 'linear-gradient(180deg, #fffbeb 0%, #fef3c7 100%)',
        border: 'rgba(217, 119, 6, 0.2)',
        rail: '#d97706',
        label: '#92400e',
      };
    case 'orange':
      return {
        background: 'linear-gradient(180deg, #fff7ed 0%, #ffedd5 100%)',
        border: 'rgba(234, 88, 12, 0.18)',
        rail: '#ea580c',
        label: '#9a3412',
      };
    case 'info':
    default: {
      const accent = accentColor || '#475569';
      return {
        background: `${accent}0d`,
        border: `${accent}26`,
        rail: accent,
        label: accent,
      };
    }
  }
}

const Banner: React.FC<BannerProps> = ({
  tone = 'info',
  label,
  accentColor,
  icon,
  rightSlot,
  compact = false,
  className = '',
  children,
}) => {
  const palette = paletteFor(tone, accentColor);
  const verticalPadding = compact ? 'py-2' : 'py-3';
  return (
    <div
      className={`relative rounded-xl px-4 ${verticalPadding} ${className}`}
      style={{ background: palette.background, border: `1px solid ${palette.border}` }}
    >
      <span
        aria-hidden
        className="absolute left-0 top-3 bottom-3 w-[2px] rounded-r"
        style={{ background: palette.rail }}
      />
      {rightSlot && <div className="absolute top-2 right-2">{rightSlot}</div>}
      {label && (
        <div
          className="text-[10px] font-semibold uppercase tracking-[0.1em] pl-2 flex items-center gap-1.5"
          style={{ color: palette.label }}
        >
          {icon}
          {label}
        </div>
      )}
      {children && (
        <div className={`text-[12.5px] text-slate-700 leading-relaxed pl-2 ${label ? 'mt-1' : ''}`}>
          {children}
        </div>
      )}
    </div>
  );
};

export default Banner;
